/**
 * "Update PO from quote" on the job page (#1629, threads/job-po-redo/).
 *
 * The estimate changed after the PO was built. One amber strip on the PO card
 * says so with three big counts (+ added, − removed, ~ changed); one button
 * opens the changes as a picture — green plus, red minus, amber "1 → 2" — and
 * one more button applies them. If the supplier already has the PO, the strip
 * says so in red and the button needs "I called them" first (the purchasing
 * rule from #1752); the result is a revised PO and the old one is marked
 * replaced. Every update can be undone from the toast while the updated PO
 * hasn't gone to the supplier. Rules live server-side in
 * src/server/services/poFromQuote.ts.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Minus, Phone, Plus, RefreshCw, Undo2 } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { KennedySplitInfo } from "@/pages/purchasing/vendor-rule-note";

interface DiffLine {
    key: string;
    description: string;
    qty: number;
}
interface ChangedLine {
    key: string;
    description: string;
    fromQty: number;
    toQty: number;
}
type Mode = "in_place" | "revise" | "blocked" | "create" | "up_to_date";
type Lane = "main" | "kennedy";
type LaneDiff = {
    lane: Lane;
    po: { id: string; poNumber: string | null; status: string; vendorName: string | null } | null;
    mode: Mode;
    blockedReason: string | null;
    diff: { added: DiffLine[]; removed: DiffLine[]; changed: ChangedLine[]; handAdded: number };
    fingerprint: string;
    callSupplierFirst: boolean;
};
export interface QuoteDiffResponse {
    po: { id: string; poNumber: string | null; status: string; vendorName: string | null } | null;
    estimate: { id: string; displayNumber: string | null } | null;
    mode: Mode | null;
    blockedReason: string | null;
    diff: { added: DiffLine[]; removed: DiffLine[]; changed: ChangedLine[]; handAdded: number } | null;
    fingerprint: string | null;
    nonOrderable: string[];
    callSupplierFirst: boolean;
    /** One per vendor's PO (the Kennedy split). Older servers omit it. */
    lanes?: LaneDiff[];
}

type ApplyResponse =
    | { ok: true; mode: "in_place" | "create"; poId: string; poNumber: string | null }
    | { ok: true; mode: "revise"; poId: string; poNumber: string | null; oldPoId: string; oldPoNumber: string | null };

const UNDO_TOAST_MS = 20_000;

const LANE_TITLE: Record<Lane, string> = { main: "Reece order", kennedy: "Test Gauge order (Kennedy)" };

export function PoQuoteUpdate({ jobId }: { jobId: string }) {
    const { data } = useQuery({
        queryKey: ["job-po-quote-diff", jobId],
        queryFn: () => api.get<QuoteDiffResponse>(`/api/purchasing/jobs/${jobId}/po-quote-diff`),
        staleTime: 30_000,
    });
    if (!data) return null;
    // One strip per vendor's PO that the quote change touches (the Kennedy
    // split). An older payload without lanes reads as one lane.
    const lanes: LaneDiff[] =
        data.lanes ??
        (data.mode && data.diff && data.fingerprint
            ? [
                  {
                      lane: "main",
                      po: data.po,
                      mode: data.mode,
                      blockedReason: data.blockedReason,
                      diff: data.diff,
                      fingerprint: data.fingerprint,
                      callSupplierFirst: data.callSupplierFirst,
                  },
              ]
            : []);
    const split = lanes.length > 1;
    const live = lanes.filter((l) => l.mode !== "up_to_date");
    if (live.length === 0) return null;
    return (
        <div className="space-y-2">
            {live.map((l) => (
                <LaneStrip
                    key={l.lane}
                    jobId={jobId}
                    lane={l}
                    title={split || l.mode === "create" ? LANE_TITLE[l.lane] : null}
                    nonOrderable={data.nonOrderable}
                    sendLane={!!data.lanes}
                />
            ))}
        </div>
    );
}

function LaneStrip({
    jobId,
    lane: data,
    title,
    nonOrderable,
    sendLane,
}: {
    jobId: string;
    lane: LaneDiff;
    title: string | null;
    nonOrderable: string[];
    /** The server sent lanes, so the apply names which one. */
    sendLane: boolean;
}) {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [called, setCalled] = useState(false);
    const vendor = data.po?.vendorName ?? (data.lane === "kennedy" ? "Test Gauge" : "the supplier");

    const refresh = () => {
        qc.invalidateQueries({ queryKey: ["job-po-quote-diff", jobId] });
        qc.invalidateQueries({ queryKey: ["job-purchase-orders", jobId] });
        qc.invalidateQueries({ queryKey: ["purchasing"] });
        // The job's parts card reads PO lines too ("on order · <PO>").
        qc.invalidateQueries({ queryKey: ["job-parts", jobId] });
    };

    const undo = useMutation({
        mutationFn: (poId: string) => api.post<{ mode: string }>(`/api/purchase-orders/${poId}/undo-quote-update`, {}),
        onSuccess: (r) => {
            refresh();
            toast.success(
                r.mode === "revise"
                    ? `Undone — the old PO is back. Call ${vendor} back: they think it's cancelled.`
                    : r.mode === "create"
                      ? "Undone — the new order is cancelled."
                      : "Undone — the PO is back the way it was.",
            );
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't undo the update."),
    });

    const apply = useMutation({
        mutationFn: () =>
            api.post<ApplyResponse>(`/api/purchasing/jobs/${jobId}/update-po-from-quote`, {
                fingerprint: data.fingerprint,
                calledSupplier: called || undefined,
                ...(sendLane ? { lane: data.lane } : {}),
            }),
        onSuccess: (r) => {
            setOpen(false);
            setCalled(false);
            refresh();
            toast.success(
                r.mode === "revise"
                    ? `Revised PO ${r.poNumber ?? ""} drafted. ${r.oldPoNumber ?? "The old PO"} is marked replaced.`
                    : r.mode === "create"
                      ? `Test Gauge PO ${r.poNumber ?? ""} drafted for the Kennedy parts.`
                      : `PO ${r.poNumber ?? ""} now matches the quote.`,
                {
                    duration: UNDO_TOAST_MS,
                    action: { label: "Undo", onClick: () => undo.mutate(r.poId) },
                },
            );
        },
        onError: (e: any) => {
            if (e?.body?.error === "QUOTE_CHANGED") refresh();
            toast.error(e?.body?.message ?? "Couldn't update the PO.");
        },
    });

    const { added, removed, changed } = data.diff;
    const revise = data.mode === "revise";
    const create = data.mode === "create";
    const heading = title ? (
        <span className="text-xs font-semibold uppercase tracking-wide text-foreground/70">{title}</span>
    ) : null;

    if (data.mode === "blocked") {
        return (
            <div
                data-testid="po-quote-blocked"
                className="flex items-start gap-2 rounded-md border border-border bg-muted/50 p-2 text-xs text-muted-foreground"
            >
                <RefreshCw className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                    {heading} <b className="text-foreground">Quote changed.</b> {data.blockedReason}
                </span>
            </div>
        );
    }

    return (
        <div
            data-testid="po-quote-update"
            className={cn(
                "rounded-md border-2 p-2",
                revise
                    ? "border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10"
                    : "border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10",
            )}
        >
            <div className="flex flex-wrap items-center gap-2">
                <RefreshCw
                    className={cn("h-4 w-4 shrink-0", revise ? "text-red-600 dark:text-red-300" : "text-amber-600")}
                />
                {heading}
                <span className="text-sm font-semibold">
                    {create ? "Kennedy parts go on their own Test Gauge order." : "Quote changed"}
                </span>
                {create && <KennedySplitInfo />}
                <span className="flex items-center gap-1.5" aria-label="Changes">
                    {added.length > 0 && <CountPill kind="add" n={added.length} />}
                    {removed.length > 0 && <CountPill kind="remove" n={removed.length} />}
                    {changed.length > 0 && <CountPill kind="change" n={changed.length} />}
                </span>
                {!open && (
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                    >
                        <RefreshCw className="h-4 w-4" /> {create ? "Make Test Gauge PO" : "Update PO from quote"}
                    </button>
                )}
            </div>

            {open && (
                <div className="mt-2 space-y-2">
                    <ul className="space-y-1" data-testid="po-quote-diff">
                        {added.map((l) => (
                            <DiffRow key={`a-${l.key}`} kind="add" text={l.description} qty={`${l.qty}`} />
                        ))}
                        {removed.map((l) => (
                            <DiffRow key={`r-${l.key}`} kind="remove" text={l.description} qty={`${l.qty}`} />
                        ))}
                        {changed.map((l) => (
                            <DiffRow
                                key={`c-${l.key}`}
                                kind="change"
                                text={l.description}
                                qty={`${l.fromQty} → ${l.toQty}`}
                            />
                        ))}
                    </ul>
                    {data.diff.handAdded > 0 && (
                        <p className="text-xs text-muted-foreground">
                            {data.diff.handAdded} line{data.diff.handAdded === 1 ? "" : "s"} typed onto the PO by hand
                            stay as they are.
                        </p>
                    )}
                    {nonOrderable.length > 0 && (
                        <p className="text-xs text-amber-800 dark:text-amber-200">
                            Not on the PO (no vendor part): {nonOrderable.join(", ")}
                        </p>
                    )}
                    {revise && (
                        <label className="flex items-start gap-2 rounded-md bg-card p-2 text-sm">
                            <input
                                type="checkbox"
                                checked={called}
                                onChange={(e) => setCalled(e.target.checked)}
                                className="mt-0.5 h-4 w-4"
                                aria-label={`I called ${vendor}`}
                            />
                            <span>
                                <Phone className="mr-1 inline h-3.5 w-3.5" />
                                <b>{vendor}</b> already has {data.po?.poNumber ?? "this PO"}. I called them to stop it.
                                A revised PO gets drafted for review, and the old one is marked replaced.
                            </span>
                        </label>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            disabled={apply.isPending || (revise && !called)}
                            onClick={() => apply.mutate()}
                            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                        >
                            <RefreshCw className="h-4 w-4" />
                            {apply.isPending
                                ? "Updating…"
                                : revise
                                  ? "Make revised PO"
                                  : create
                                    ? "Make Test Gauge PO"
                                    : "Update PO"}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(false);
                                setCalled(false);
                            }}
                            className="rounded-md px-3 py-1.5 text-sm hover:bg-accent"
                        >
                            Not now
                        </button>
                        <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Undo2 className="h-3 w-3" /> You can undo it
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}

const KIND_STYLE = {
    add: {
        icon: Plus,
        box: "bg-emerald-600 text-white",
        pill: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200",
        label: "added",
    },
    remove: {
        icon: Minus,
        box: "bg-red-600 text-white",
        pill: "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-200",
        label: "removed",
    },
    change: {
        icon: RefreshCw,
        box: "bg-amber-500 text-white",
        pill: "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200",
        label: "changed",
    },
} as const;

function CountPill({ kind, n }: { kind: keyof typeof KIND_STYLE; n: number }) {
    const s = KIND_STYLE[kind];
    const Icon = s.icon;
    return (
        <span
            className={cn("inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-sm font-bold", s.pill)}
            title={`${n} ${s.label}`}
        >
            <Icon className="h-3.5 w-3.5" />
            {n}
        </span>
    );
}

function DiffRow({ kind, text, qty }: { kind: keyof typeof KIND_STYLE; text: string; qty: string }) {
    const s = KIND_STYLE[kind];
    const Icon = s.icon;
    return (
        <li className="flex items-center gap-2 rounded-md bg-card px-2 py-1.5 text-sm" data-kind={kind}>
            <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded", s.box)}>
                <Icon className="h-4 w-4" />
            </span>
            <span className={cn("min-w-0 flex-1 truncate", kind === "remove" && "line-through opacity-70")}>
                {text}
            </span>
            <span className="shrink-0 font-mono text-sm font-semibold tabular-nums">× {qty}</span>
        </li>
    );
}
